"use client";

import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface PanelCopy {
  eyebrow: string;
  title: string;
  lead: string;
  point1: string;
  point2: string;
  point3: string;
  fact1Label: string;
  fact1Value: string;
  fact2Label: string;
  fact2Value: string;
  fact3Label: string;
  fact3Value: string;
  fact4Label: string;
  fact4Value: string;
  imgAlt: string;
}
const copyData: Record<Lang, PanelCopy> = {
  en: {
    eyebrow: "Pharmaceutical Applications",
    title: "Pharmaceutical Process Filtration",
    lead: "Engineered stainless steel sintered laminate and precision woven mesh media provide non-shedding, pore-stable separation for active pharmaceutical ingredients (APIs) and high-purity process streams.",
    point1: "Diffusion-bonded sintered laminate and compacted Dutch weaves guaranteeing zero particle migration into critical drug streams.",
    point2: "High-integrity 316L and Hastelloy C-22 construction resisting corrosive synthesis solvents and cyclic autoclave sterilization.",
    point3: "Bubble point verified pore stability down to 1 µm for sterile filtration, API crystallization, and parenteral fluid polishing.",
    fact1Label: "Alloy Grades",
    fact1Value: "316L / C-22",
    fact2Label: "Rating Range",
    fact2Value: "1–100 µm",
    fact3Label: "Sanitary Integrity",
    fact3Value: "Non-Shedding",
    fact4Label: "Cleanability",
    fact4Value: "CIP / SIP Steam",
    imgAlt: "Precision sintered stainless steel multilayer filter element for pharmaceutical processing"
  },
  zh: {
    eyebrow: "制药工艺应用",
    title: "制药工艺精密过滤",
    lead: "精密不锈钢烧结复合网与编织网介质，为原料药（API）提纯及高纯流体工艺提供孔径稳定且无纤维脱落的固液分离保障。",
    point1: "扩散粘结烧结复合网与紧密密纹滤布，杜绝任何颗粒杂质或纤维脱落进入药液流。",
    point2: "优质 316L 及哈氏合金 C-22 结构，耐受酸性合成溶剂腐蚀与高频高压蒸汽灭菌。",
    point3: "泡点法验证微孔结构稳定性，过滤精度可达 1 µm，适用于无菌精滤与结晶母液回收。",
    fact1Label: "合金牌号",
    fact1Value: "316L / C-22",
    fact2Label: "过滤精度",
    fact2Value: "1–100 µm",
    fact3Label: "卫生级结构",
    fact3Value: "零纤维脱落",
    fact4Label: "清洗灭菌",
    fact4Value: "CIP / SIP 蒸汽",
    imgAlt: "用于制药工艺的高精度多层不锈钢烧结滤芯结构"
  },
  es: {
    eyebrow: "Aplicaciones farmacéuticas",
    title: "Filtración en procesos farmacéuticos",
    lead: "Los medios de laminado sinterizado y malla tejida de precisión brindan separación estable y sin desprendimiento para principios activos farmacéuticos (API) y fluidos de alta pureza.",
    point1: "Laminados sinterizados y tejidos Dutch compactos que garantizan cero migración de partículas hacia el producto final.",
    point2: "Construcción en 316L y Hastelloy C-22 resistente a solventes corrosivos de síntesis y esterilización en autoclave.",
    point3: "Estabilidad de poros verificada por punto de burbuja hasta 1 µm para filtración estéril y purificación de API.",
    fact1Label: "Grados de aleación",
    fact1Value: "316L / C-22",
    fact2Label: "Rango de filtración",
    fact2Value: "1–100 µm",
    fact3Label: "Integridad sanitaria",
    fact3Value: "Sin desprendimiento",
    fact4Label: "Limpieza",
    fact4Value: "Vapor CIP / SIP",
    imgAlt: "Elemento filtrante multicapa de acero inoxidable sinterizado de precisión para procesos farmacéuticos"
  },
  de: {
    eyebrow: "Pharmazeutische Anwendungen",
    title: "Filtration in pharmazeutischen Prozessen",
    lead: "Präzisions-Sinterverbund- und Drahtgewebemedien aus Edelstahl bieten partikelfreie, porenstabile Fest-Flüssig-Trennung für pharmazeutische Wirkstoffe (APIs) und Reinstprozessströme.",
    point1: "Diffusionsgebundene Sinterlaminate und dichte Tressengewebe verhindern jeglichen Partikelabrieb in kritische Prozessströme.",
    point2: "Robuste Ausführung in 316L und Hastelloy C-22 beständig gegen korrosive Lösungsmittel und Autoklavenzyklen.",
    point3: "Blaspunkt-geprüfte Porenstabilität bis 1 µm für Sterilfiltration, API-Kristallisation und parenterale Polierfiltration.",
    fact1Label: "Legierungsgüten",
    fact1Value: "316L / C-22",
    fact2Label: "Filterfeinheitsbereich",
    fact2Value: "1–100 µm",
    fact3Label: "Sanitäre Integrität",
    fact3Value: "Partikelfrei",
    fact4Label: "Reinigbarkeit",
    fact4Value: "CIP / SIP Dampf",
    imgAlt: "Präzisions-Edelstahl-Mehrlagenfilterelement für pharmazeutische Prozesse"
  },
  fr: {
    eyebrow: "Applications pharmaceutiques",
    title: "Filtration pour procédés pharmaceutiques",
    lead: "Les médias en stratifié fritté et toiles tissées de précision assurent une séparation stable et sans relargage de fibres pour les principes actifs pharmaceutiques (API) et fluides de haute pureté.",
    point1: "Stratifiés frittés par diffusion et tissages hollandais compacts garantissant zéro migration de média.",
    point2: "Construction en 316L et Hastelloy C-22 résistant aux solvants corrosifs de synthèse et à la stérilisation autoclave.",
    point3: "Stabilité des pores vérifiée par point de bulle jusqu'à 1 µm pour filtration stérile et cristallisation d'API.",
    fact1Label: "Nuances d'alliages",
    fact1Value: "316L / C-22",
    fact2Label: "Plage de filtration",
    fact2Value: "1–100 µm",
    fact3Label: "Intégrité sanitaire",
    fact3Value: "Sans relargage",
    fact4Label: "Nettoyabilité",
    fact4Value: "Vapeur NEP / SEP",
    imgAlt: "Élément filtrant multicouche en acier inoxydable fritté de précision pour procédés pharmaceutiques"
  },
  pt: {
    eyebrow: "Aplicações farmacêuticas",
    title: "Filtração para processos farmacêuticos",
    lead: "Laminados sinterizados e telas tecidas de precisão oferecem separação com porosidade estável e sem desprendimento de partículas para princípios ativos (APIs) e fluidos de alta pureza.",
    point1: "Laminados sinterizados e tramas holandesas densas garantindo zero migração de partículas para o fluxo de processo.",
    point2: "Construção de alta integridade em 316L e Hastelloy C-22 resistente a solventes corrosivos e autoclavação cíclica.",
    point3: "Porosidade verificada por ponto de bolha até 1 µm para filtração estéril, cristalização de API e polimento parenteral.",
    fact1Label: "Graus de liga",
    fact1Value: "316L / C-22",
    fact2Label: "Faixa de filtração",
    fact2Value: "1–100 µm",
    fact3Label: "Integridade sanitária",
    fact3Value: "Sem desprendimento",
    fact4Label: "Facilidade de limpeza",
    fact4Value: "Vapor CIP / SIP",
    imgAlt: "Elemento filtrante multicamada em aço inoxidável sinterizado de precisão para processos farmacêuticos"
  }
};
export default function PharmaceuticalPanel() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  const facts: VariantFact[] = [{
    label: c.fact1Label,
    value: c.fact1Value
  }, {
    label: c.fact2Label,
    value: c.fact2Value
  }, {
    label: c.fact3Label,
    value: c.fact3Value
  }, {
    label: c.fact4Label,
    value: c.fact4Value
  }];
  const points = [c.point1, c.point2, c.point3];
  return <VariantPanel id="pharmaceutical" eyebrow={c.eyebrow} title={c.title} lead={c.lead} points={points} facts={facts} image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
    alt: c.imgAlt
  }} tone="muted" reverse={true} href="/PharmaceuticalFiltrationPage" />;
}